/**
 * The one list of file kinds the app tells apart when a person adds files:
 * images become image frames, PDFs file frames that show their pages,
 * markdown and plain text editable Docs, anything else a plain file card.
 * The size limits mirror `backend/apps/uploads/models.py`.
 */

export type FileKind = "image" | "pdf" | "doc" | "file";

export const PDF_MIME = "application/pdf";

/** Raster images the backend validates and resizes (`ALLOWED_CONTENT_TYPES`). */
const IMAGE_MIME_TYPES: ReadonlySet<string> = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
]);
const IMAGE_EXTENSIONS: ReadonlySet<string> = new Set([
  "png",
  "jpg",
  "jpeg",
  "webp",
  "gif",
]);
export const PDF_EXTENSIONS: ReadonlySet<string> = new Set(["pdf"]);
const TEXT_DOC_EXTENSIONS: ReadonlySet<string> = new Set([
  "md",
  "markdown",
  "txt",
]);
const MARKDOWN_MIME_TYPES: ReadonlySet<string> = new Set([
  "text/markdown",
  "text/x-markdown",
]);
/** A MIME type that says nothing; the extension decides. */
const GENERIC_MIME_TYPES: ReadonlySet<string> = new Set([
  "",
  "application/octet-stream",
]);
/** Documents a browser would run: never rendered, always a plain file. */
const ACTIVE_MIME = /html|svg|xml|javascript/;
const ACTIVE_EXTENSIONS: ReadonlySet<string> = new Set([
  "svg",
  "svgz",
  "html",
  "htm",
  "xhtml",
  "xht",
  "xml",
  "mht",
  "mhtml",
]);

/** Images, checked before the backend's resize (`MAX_FILE_SIZE`). */
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
/** Every other file (`MAX_ATTACHMENT_SIZE`). */
const MAX_FILE_BYTES = 20 * 1024 * 1024;

/** A file name's extension in lower case, or "" without one. */
export function fileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
}

/** A file name without its extension, or the name when it has none. */
export function fileStem(name: string): string {
  const extension = fileExtension(name);
  return extension ? name.slice(0, -extension.length - 1) : name;
}

/**
 * What a file becomes: by its MIME type when that names a kind, else (no
 * type, or a generic one) by its extension. HTML, SVG and other active
 * documents are always plain files.
 */
export function fileKind(file: Pick<File, "name" | "type">): FileKind {
  const mime = file.type.split(";", 1)[0]!.trim().toLowerCase();
  const extension = fileExtension(file.name);
  if (ACTIVE_MIME.test(mime) || ACTIVE_EXTENSIONS.has(extension)) {
    return "file";
  }
  if (IMAGE_MIME_TYPES.has(mime)) return "image";
  if (mime === PDF_MIME) return "pdf";
  if (MARKDOWN_MIME_TYPES.has(mime)) return "doc";
  if (mime === "text/plain") {
    return TEXT_DOC_EXTENSIONS.has(extension) ? "doc" : "file";
  }
  if (!GENERIC_MIME_TYPES.has(mime)) return "file";
  if (IMAGE_EXTENSIONS.has(extension)) return "image";
  if (PDF_EXTENSIONS.has(extension)) return "pdf";
  if (TEXT_DOC_EXTENSIONS.has(extension)) return "doc";
  return "file";
}

/** The size limit for a file of `kind`. */
export function maxFileBytes(kind: FileKind): number {
  return kind === "image" ? MAX_IMAGE_BYTES : MAX_FILE_BYTES;
}
